import React, { FunctionComponent, useEffect, useState } from 'react'
import Button from '@material-ui/core/Button'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Grid from '@material-ui/core/Grid'
import useFormal from '@kevinwolf/formal-web'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useApi } from 'components/providers/ApiProvider'
import { useMessage } from 'components/elements/Message'
import { DraggableDialog, DraggableDialogTitle } from 'components/elements/DraggableDialog'
import { TextField } from 'components/elements/TextField'

export interface EditClientNotesDialogData {
  permission_id: number
  notes: string
}

type Props = {
  data: EditClientNotesDialogData
}
type Functions = {
  onSuccess(): void
}
type AllProps = Props & Functions

const EditClientNotesDialog: FunctionComponent<AllProps> = ({ data, onSuccess }) => {
  if (!data) {
    return null
  }

  const { t } = useTranslation()
  const api = useApi()
  const { showSuccess, showError } = useMessage()

  const [open, setOpen] = useState(false)

  useEffect(() => {
    setOpen(true)
  }, [data])

  const handleEnter = () => {
    formal.change('notes', data.notes)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleExited = () => {
    formal.change('notes', initialValues.notes)
    formal.clearErrors()
  }

  const initialValues = {
    notes: '',
  }

  const schema = yup.object().shape({
    notes: yup.string().trim().max(200),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      values = schema.cast(values)

      await api.modifyReservationOfferClientNotes({
        permission_id: data.permission_id,
        text: values.notes,
      })
        .then(data => {
          if (!data || !data.status) {
            throw new Error
          }

          handleClose()
          onSuccess()

          showSuccess(t('messages.reservationOfferClientNotesUpdated'))
        })
        .catch(error => {
          let message

          switch (error.code) {
            case 3:
              message = t('alerts.checkInput')
              break
            default:
              message = t('alerts.tryLater')
          }

          showError(`${t('alerts.couldNotUpdateReservationOfferClientNotes')} ${message}`)
        })
    },
  })

  return (
    <DraggableDialog fullWidth open={open} onClose={handleClose} TransitionProps={{ onEnter: handleEnter, onExited: handleExited }}>
      <form {...formal.getFormProps()} noValidate>
        <DraggableDialogTitle>{t('editNotes')}</DraggableDialogTitle>
        <DialogContent>
          <Grid container>
            <TextField
              GridProps={{ xs: 12 }}
              label={t('notes')}
              inputProps={{ maxLength: 200 }}
              autoFocus={true}
              {...formal.getFieldProps('notes')}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={formal.isSubmitting}
          >
            {t('submit')}
          </Button>
        </DialogActions>
      </form>
    </DraggableDialog>
  )
}

export default EditClientNotesDialog
